<template>
  <div class="rate-finder-wrapper" v-if="!userLocationLoading">
    <country-selector
      v-if="showCountrySelector"
      v-model:selectedCountry="selectedCountry"
      :defaultCountries="countries"
    />
    <rate-card
      v-if="allRates"
      :selectedCountryRates="allRates"
      :selectedAmount="amount"
      :singleRate="singleRate"
      :currency="selectedCurrency"
      :isBuyCredit="showRateListCard"
      :extra="extra?.amount"
    />
    <rate-list-card
      v-if="showRateListCard"
      :selectedCountryRates="allRates"
      :currency="selectedCurrency"
      @update:selectRate="selectedRate"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, onUnmounted, PropType } from 'vue';
import CountrySelector from '@/components/CountrySelector.vue';
import RateCard from '@/components/RateCard.vue';
import RateListCard from '@/components/RateListCard.vue';
import { fetchRates } from '@/api/rates';
import { default as countriesJson } from '@/data/countries.json';
import type { RateListItem, CountryRate } from '@/types/rates';
import type { NamedCountry } from '@/models/namedcountry';
import { fetchUserLocation } from './api/location';
import { fetchCountries } from '@/api/countries';
import { Country } from './models/country';

export default defineComponent({
  name: 'RateFinder',
  props: {
    defaultCountry: {
      type: String,
      required: false,
    },
    defaultCurrency: {
      type: String,
      required: false,
    },
    showRateListCard: {
      type: Boolean,
      required: false,
      default: true,
    },
    amount: {
      type: Number,
      required: false,
    },
    extra: {
      type: [Object, null] as PropType<{ amount: number | null; percentage: number | null } | null>,
      required: false,
      default: () => ({ amount: null, percentage: null }),
    },
  },
  components: {
    CountrySelector,
    RateCard,
    RateListCard,
  },
  setup(props) {
    const countries = ref<Country[]>(countriesJson);
    const selectedCountry = ref<string | NamedCountry>(
      props.defaultCountry
        ? (countries.value.find(({ code }) => props.defaultCountry?.toLowerCase() === code) as unknown as NamedCountry)
        : '',
    );
    const selectedCurrency = ref(
      (document.querySelector('#currency') as HTMLSelectElement | null)?.value || props.defaultCurrency,
    );
    const showCountrySelector = ref<boolean>(true);
    const allRates = ref<CountryRate | null>(null);
    const singleRate = ref<RateListItem | null>(null);
    const controller = ref();
    const userLocationLoading = ref<boolean>(true);
    const mzCountriesLoading = ref<boolean>(true);

    const selectedCountryRates = computed(() => {
      const country = selectedCountry.value || props.defaultCountry;
      const countryCode = typeof country === 'object' && 'code' in country ? country.code : country;

      return countryCode;
    });

    const getCountryRates = async () => {
      if (!selectedCountryRates.value || !selectedCurrency.value) return;
      singleRate.value = null;
      try {
        if (controller.value) {
          controller.value.abort();
        }
        controller.value = new AbortController();
        allRates.value = await fetchRates(
          selectedCountryRates.value.toUpperCase(),
          selectedCurrency.value,
          controller.value.signal,
        );
      } catch (error) {
        console.error('Failed to fetch rates:', error);
      }
    };

    watch(
      () => selectedCountry.value,
      () => {
        singleRate.value = null;
        getCountryRates();
      },
      { immediate: true },
    );

    const handleCurrencyChange = async () => {
      const currency = (document.querySelector('#currency') as HTMLSelectElement | null)?.value;
      if (!currency) {
        return;
      }
      selectedCurrency.value = currency;
      getCountryRates();
    };

    onMounted(async () => {
      const wrapperContainer = document.getElementById('shop-component');
      const defaultCountry = wrapperContainer?.getAttribute('defaultCountry');
      const _showCountrySelector = wrapperContainer?.getAttribute('showCountrySelector');
      showCountrySelector.value = _showCountrySelector !== 'false';

      if (defaultCountry) {
        selectedCountry.value = defaultCountry;
        userLocationLoading.value = false;
      } else {
        getUserLocation();
      }

      if (showCountrySelector.value) {
        try {
          mzCountriesLoading.value = true;
          const mzCountries = await fetchCountries();
          const filteredCountries = countries.value.filter(({ code }) => mzCountries.includes(code.toUpperCase()));
          countries.value = filteredCountries;
          mzCountriesLoading.value = false;
        } catch (error) {
          mzCountriesLoading.value = false;
          console.log('Error getting MZ countries');
        }
      }
      document.querySelector('#currency')?.addEventListener('change', handleCurrencyChange);
    });

    onUnmounted(() => {
      document.querySelector('#currency')?.removeEventListener('change', handleCurrencyChange);
    });

    const selectedRate = (rate: RateListItem) => {
      singleRate.value = rate;
    };

    const getUserLocation = async () => {
      try {
        userLocationLoading.value = true;
        const userCountry = await fetchUserLocation();
        const foundCountry = countries.value.find(
          ({ code }) => userCountry.country_code.toLowerCase() === code.toLowerCase(),
        );
        if (foundCountry) selectedCountry.value = foundCountry.code;
        userLocationLoading.value = false;
      } catch (error) {
        userLocationLoading.value = false;
        console.log('Error getting user location');
      }
    };

    return {
      countries,
      selectedCountry,
      selectedCurrency,
      allRates,
      showCountrySelector,
      singleRate,
      userLocationLoading,
      selectedRate,
    };
  },
});
</script>

<style scoped lang="scss">
@import './fonts';

.rate-finder-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  & * {
    font-family: 'Noto Sans', sans-serif !important;
  }
}
</style>
